import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Badge, Search } from '@indipia/ui-core';
import type { BasicInfoPartner } from '../types/worklog.types';

/**
 * [Temp — D-07 §3.5] 작업요청 작성 — 작업자 선택(1명).
 * 표준 Combobox(§15.36)는 선택칸 안 배지 · 목록 머리글 · 2열 보조 텍스트를 담지 못해, 같은 화면의 작업공종 선택(WorkTypeSelect)과
 * 같은 구조(.field.combo > .input 트리거 + .menu > 머리글 + .menu-item)로 만든다. 표준 CSS·토큰만 쓰고 새 스타일은 없다.
 * 차이: 선택칸·항목에 '자재파트너' Badge(§15.5, SIF-1 자재시공 공종), 머리글 아래 Search(§15.13).
 */

/** 작업자 후보 한 명 — detail = 오른쪽 보조 텍스트(시공파트너: 전문분야 · 연락처, 자재파트너: '자재파트너' · 공종 · 연락처) */
export interface WorkerSelectOption {
  partner: BasicInfoPartner;
  /** 자재시공 공종의 자재파트너면 true — 이름 옆 '자재파트너' 배지 */
  material: boolean;
  detail: string;
}

const HEADER = '작업자 선택 (시공파트너 · 자재파트너)';

export function Temp_WorkerSelect({
  value,
  valueIsMaterial,
  options,
  disabled,
  placeholder,
  emptyText,
  onChange,
}: {
  value: BasicInfoPartner | null;
  valueIsMaterial: boolean;
  options: WorkerSelectOption[];
  disabled?: boolean;
  placeholder: string;
  emptyText: string;
  onChange: (partner: BasicInfoPartner) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => `${o.partner.partnerName} ${o.detail}`.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const closeMenu = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };
  const select = (o: WorkerSelectOption) => {
    onChange(o.partner);
    closeMenu();
  };

  // 바깥 클릭 닫기 — WorkTypeSelect 와 같은 방식(문서 mousedown)
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (disabled) closeMenu();
  }, [disabled]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open && activeIndex >= 0 && filtered[activeIndex]) {
      e.preventDefault();
      select(filtered[activeIndex]);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      closeMenu();
    }
  };

  return (
    <div ref={rootRef} className={`field combo !max-w-none flex-1${open ? ' open' : ''}`} onKeyDown={onKeyDown}>
      <button
        type="button"
        className="input flex items-center"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => (open ? closeMenu() : setOpen(true))}
      >
        <span className="flex min-w-0 flex-1 items-center gap-sm text-left">
          {value ? (
            <>
              <span className="min-w-0 truncate font-medium">{value.partnerName}</span>
              {valueIsMaterial && <Badge status="info">자재파트너</Badge>}
            </>
          ) : (
            <span className="min-w-0 truncate text-text-muted">{placeholder}</span>
          )}
        </span>
        <span className="caret" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="menu show" role="listbox" id={listId}>
          <div className="mb-02 whitespace-nowrap rounded-md bg-surface-muted px-03 py-02 text-caption font-bold text-text-muted">{HEADER}</div>
          <div className="mb-02">
            <Search
              value={query}
              onChange={(v) => {
                setQuery(v);
                setActiveIndex(-1);
              }}
              onClear={() => setQuery('')}
              placeholder="이름 · 연락처 · 공종 검색"
              width="fill"
              size="sm"
              autoFocus
            />
          </div>
          {filtered.length === 0 && <div className="combo-empty">{query.trim() ? '검색 결과가 없습니다' : emptyText}</div>}
          {filtered.map((o, i) => {
            const isSelected = value?.partnerId === o.partner.partnerId;
            return (
              <div
                key={o.partner.partnerId}
                role="option"
                aria-selected={isSelected}
                className={`menu-item justify-between gap-md${isSelected ? ' selected' : ''}${i === activeIndex ? ' active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(o)}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <span className="flex min-w-0 items-center gap-sm">
                  <span className="min-w-0 truncate">{o.partner.partnerName}</span>
                  {o.material && <Badge status="info">자재파트너</Badge>}
                </span>
                <span className="shrink-0 text-caption font-normal text-text-muted">{o.detail}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
